(function () {
  'use strict';

  if (!window.algoliaConfig) {
    console.warn('All News: Algolia config not found!');
    return;
  }

  const { appId, searchKey, indexName } = window.algoliaConfig;

  if (!appId || !searchKey || !indexName) {
    console.warn('All News: Algolia credentials incomplete');
    return;
  }

  // Shared attribute list — matches algolia-search.js
  const SEARCH_ATTRIBUTES = [
    'objectID', 'title', 'url', 'handle',
    'image', 'featured_image', 'published_at',
    'created_at', 'author', 'blog_handle', 'blog'
  ];

  let algoliaIndex = null;
  let currentFilters = { blog: '' };
  let currentPage = 0;
  let totalPages = 0;
  let resultsPerPage = 24;
  let allowedTags = [];
  
  function initAllNews() {
    const component = document.querySelector('[data-all-news-algolia]');
    if (!component) return;

    if (typeof algoliasearch === 'undefined') {
      setTimeout(initAllNews, 100);
      return;
    }

    const client = algoliasearch(appId, searchKey);
    algoliaIndex = client.initIndex(indexName);

    // Get configuration from data attributes
    resultsPerPage = parseInt(component.dataset.resultsPerPage, 10) || 24;

    // Parse allowed tags from setting (comma-separated list)
    const allowedTagsStr = component.dataset.allowedTags || '';
    if (allowedTagsStr.trim()) {
      allowedTags = allowedTagsStr.split(',').map(tag => tag.trim()).filter(Boolean);
      console.log('[All News] Allowed tags:', allowedTags);
    }

    // Parse URL for initial state
    parseURLState();

    // Reflect URL filter state in chip active classes immediately 
    if (currentFilters.blog) {
      document.querySelectorAll('.all-news-filter__chip[data-blog]').forEach(chip => {
        chip.classList.toggle('active', chip.dataset.blog === currentFilters.blog);
      });
    }

    // Setup event listeners
    setupFilterListeners();

    // Perform initial search (fast page fetch, then background full load)
    performSearch();

    // Handle browser navigation
    window.addEventListener('popstate', () => {
      parseURLState();
      performSearch();
    });

    console.log('All News Algolia initialized');
  }

  function parseURLState() {
    const params = new URLSearchParams(window.location.search);
    currentFilters.blog = params.get('blog') || '';
    currentPage = Math.max(0, parseInt(params.get('page') || '1', 10) - 1);
  }

  function updateURL() {
    const params = new URLSearchParams();

    if (currentFilters.blog) {
      params.set('blog', currentFilters.blog);
    }
    if (currentPage > 0) {
      params.set('page', currentPage + 1);
    }

    const newURL = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.pushState({}, '', newURL);
  }


  // Check if a hit is an article (has blog or blog_handle)
  function isArticle(hit) {
    return hit.blog || hit.blog_handle;
  }

  // Cache for all articles to avoid re-fetching on filter/page change
  let allArticlesCache = null;
  let blogCountsCache = null;

  // Browse the entire Algolia index using the cursor-based Browse API.
  // Unlike search(), which caps at 1000 records, browse has no limit.
  async function browseAllRecords() {
    const allHits = [];
    const browseUrl = `https://${appId}-dsn.algolia.net/1/indexes/${encodeURIComponent(indexName)}/browse`;
    const headers = {
      'X-Algolia-Application-Id': appId,
      'X-Algolia-API-Key': searchKey,
      'Content-Type': 'application/json'
    };

    const params = new URLSearchParams({
      query: '',
      hitsPerPage: '1000',
      attributesToRetrieve: SEARCH_ATTRIBUTES.join(',')
    });

    let body = JSON.stringify({ params: params.toString() });

    while (true) {
      const response = await fetch(browseUrl, { method: 'POST', headers, body });
      if (!response.ok) throw new Error(`Browse API ${response.status}`);

      const data = await response.json();
      allHits.push(...data.hits);
      console.log(`[All News] Browsed ${allHits.length}/${data.nbHits || '?'} records`);

      if (!data.cursor) break;
      body = JSON.stringify({ cursor: data.cursor });
    }

    return allHits;
  }

  // Fallback: paginate via search(). Capped at 1000 records by Algolia.
  async function searchAllRecords() {
    const allHits = [];
    let page = 0;

    while (true) {
      const response = await algoliaIndex.search('', {
        hitsPerPage: 100,
        page: page,
        attributesToRetrieve: SEARCH_ATTRIBUTES
      });

      allHits.push(...response.hits);
      console.log(`[All News] Search page ${page + 1}/${response.nbPages}, collected: ${allHits.length}/${response.nbHits}`);

      if (page >= response.nbPages - 1 || response.hits.length === 0) break;
      page++;
    }

    return allHits;
  }

  async function fetchAllArticles() {
    // Return cached results if available
    if (allArticlesCache) {
      console.log('[All News] Using cached articles:', allArticlesCache.length);
      return { articles: allArticlesCache, blogCounts: blogCountsCache };
    }

    console.log('[All News] Fetching ALL articles from Algolia...');

    let allHits;
    try {
      allHits = await browseAllRecords();
    } catch (error) {
      console.warn('[All News] Browse API unavailable, falling back to search (max 1000 records):', error.message);
      allHits = await searchAllRecords();
    }

    console.log('[All News] Total records fetched from Algolia:', allHits.length);

    // Filter to only articles
    const articles = allHits.filter(hit => isArticle(hit));
    console.log('[All News] Total articles found:', articles.length);

    // Sort by published date (newest first)
    articles.sort((a, b) => {
      const dateA = new Date(a.published_at || a.created_at || 0);
      const dateB = new Date(b.published_at || b.created_at || 0);
      return dateB - dateA;
    });

    // Build blog counts
    const blogCounts = {};
    articles.forEach(hit => {
      const blogHandle = getBlogHandle(hit);
      if (blogHandle) {
        blogCounts[blogHandle] = (blogCounts[blogHandle] || 0) + 1;
      }
    });

    // Cache results
    allArticlesCache = articles;
    blogCountsCache = blogCounts;
    
    return { articles, blogCounts };
  }

  // Fast fetch: get a batch from Algolia, sort by date, and return the current page.
  // We over-fetch (up to 200 records) so client-side date sorting gives accurate newest-first results.
  const INITIAL_FETCH_SIZE = 200;

  async function fetchCurrentPage() {
    // First try: fetch without Algolia-side filtering (always works)
    const response = await algoliaIndex.search('', {
      hitsPerPage: INITIAL_FETCH_SIZE,
      page: 0,
      attributesToRetrieve: SEARCH_ATTRIBUTES
    });

    // Filter to articles only, then sort newest first
    let articles = response.hits.filter(hit => isArticle(hit));
    articles.sort((a, b) => {
      const dateA = new Date(a.published_at || a.created_at || 0);
      const dateB = new Date(b.published_at || b.created_at || 0);
      return dateB - dateA;
    });

    // Apply blog filter client-side (safe — doesn't depend on Algolia facet config)
    if (currentFilters.blog) {
      articles = articles.filter(hit => {
        const blogHandle = getBlogHandle(hit);
        return blogHandle === currentFilters.blog;
      });
    }

    // Slice to the requested page
    const startIndex = currentPage * resultsPerPage;
    const pageHits = articles.slice(startIndex, startIndex + resultsPerPage);
    const estimatedTotalPages = Math.ceil(articles.length / resultsPerPage);

    return {
      hits: pageHits,
      totalHits: articles.length,
      totalPages: estimatedTotalPages
    };
  }

  // Flag to track if background load is in progress
  let backgroundLoadPromise = null;
  // Track the page/filter state when initial render happened, to avoid unnecessary re-render
  let initialRenderState = null;

  async function performSearch() {
    const gridContainer = document.getElementById('all-news-grid');
    if (!gridContainer || !algoliaIndex) return;

    // If we already have the full cache, use it directly (fast path for filter/page changes)
    if (allArticlesCache) {
      gridContainer.classList.add('loading');
      try {
        renderFromCache();
      } finally {
        gridContainer.classList.remove('loading');
      }
      return;
    }

    // If a filter is active on first load (e.g. user reloaded with ?blog=X in URL),
    // skip the fast fetch — it can't reliably filter from 200 records — and go straight
    // to the full background browse so we always show correct results.
    if (currentFilters.blog && !allArticlesCache) {
      gridContainer.classList.add('loading');
      try {
        console.log('[All News] Filter active on load, waiting for full cache...');
        const { blogCounts } = await fetchAllArticles();

        document.querySelectorAll('.all-news-filter__chip[data-blog]').forEach(chip => {
          chip.disabled = false;
        });
        if (Object.keys(blogCounts).length > 0) {
          updateBlogFilterOptions(blogCounts);
        }

        gridContainer.classList.remove('loading');
        renderFromCache();
      } catch (error) {
        console.error('[All News] Search error:', error);
        gridContainer.classList.remove('loading');
        gridContainer.innerHTML = `<p class="all-news-error">Error loading articles: ${error.message}</p>`;
      }
      return;
    }

    // Fetch only the current page for fast display
    try {
      console.log('[All News] Fast fetch: page', currentPage + 1, currentFilters.blog ? '(filter: ' + currentFilters.blog + ')' : '');
      const pageData = await fetchCurrentPage();

      gridContainer.classList.remove('loading');

      totalPages = pageData.totalPages;
      renderResults(pageData.hits, pageData.totalHits);
      renderPagination();
      updateURL();

      // Save the state of what we just rendered
      initialRenderState = { page: currentPage, blog: currentFilters.blog };

      console.log('[All News] Page rendered, loading rest in background...');

      // Load all articles in the background for accurate pagination
      if (!backgroundLoadPromise) {
        backgroundLoadPromise = fetchAllArticles().then(({ blogCounts }) => {
          // Enable filter chips now that cache is ready
          document.querySelectorAll('.all-news-filter__chip[data-blog]').forEach(chip => {
            chip.disabled = false;
          });

          // Update filter chips with full category data
          if (Object.keys(blogCounts).length > 0) {
            updateBlogFilterOptions(blogCounts);
          }

          // Only update pagination totals — don't re-render article cards
          // unless the user has already navigated away from the initial view
          const userStillOnInitialView = initialRenderState
            && initialRenderState.page === currentPage
            && initialRenderState.blog === currentFilters.blog;

          if (userStillOnInitialView) {
            // Just update pagination with accurate totals, keep articles as-is
            let articleHits = allArticlesCache;
            if (currentFilters.blog) {
              articleHits = allArticlesCache.filter(hit => getBlogHandle(hit) === currentFilters.blog);
            }
            totalPages = Math.ceil(articleHits.length / resultsPerPage);
            renderPagination();
            console.log('[All News] Background load complete — pagination updated, articles unchanged');
          } else {
            // User navigated, render the full cached view
            renderFromCache();
            console.log('[All News] Background load complete — full re-render (user navigated)');
          }
        }).catch(err => {
          console.warn('[All News] Background load failed:', err.message);
        }).finally(() => {
          backgroundLoadPromise = null;
          initialRenderState = null;
        });
      }

    } catch (error) {
      console.error('[All News] Search error:', error);
      gridContainer.classList.remove('loading');
      gridContainer.innerHTML = `<p class="all-news-error">Error loading articles: ${error.message}</p>`;
    }
  }

  // Render results from the full cached data (used after background load & for filter/page changes)
  function renderFromCache() {
    if (!allArticlesCache) return;

    let articleHits = allArticlesCache;
    if (currentFilters.blog) {
      articleHits = allArticlesCache.filter(hit => {
        const blogHandle = getBlogHandle(hit);
        return blogHandle === currentFilters.blog;
      });
    }

    totalPages = Math.ceil(articleHits.length / resultsPerPage);
    const startIndex = currentPage * resultsPerPage;
    const pageHits = articleHits.slice(startIndex, startIndex + resultsPerPage);

    renderResults(pageHits, articleHits.length);
    renderPagination();
    updateURL();

    if (blogCountsCache && Object.keys(blogCountsCache).length > 0) {
      updateBlogFilterOptions(blogCountsCache);
    }
  }

  // Helper to get blog handle from hit (handles different data structures)
  function getBlogHandle(hit) {
    if (hit.blog_handle) return hit.blog_handle;
    if (hit.blog && hit.blog.handle) return hit.blog.handle;
    if (hit.blog && typeof hit.blog === 'string') return hit.blog;
    return null;
  }

  function renderResults(hits, totalArticles) {
    const gridContainer = document.getElementById('all-news-grid');
    if (!gridContainer) return;

    // Update clear button visibility
    const clearBtn = document.getElementById('all-news-clear-filters');
    if (clearBtn) {
      clearBtn.style.display = currentFilters.blog ? 'inline-block' : 'none';
    }

    if (hits.length === 0) {
      gridContainer.innerHTML = `
        <div class="all-news-no-results">
          <p>No articles found</p>
          ${currentFilters.blog ? '<p>Try selecting a different category or clear filters.</p>' : '<p>Please check back later for new content.</p>'}
        </div>
      `;
      return;
    }

    const cardsHtml = hits.map(hit => renderArticleCard(hit)).join('');
    gridContainer.innerHTML = cardsHtml;
  }

  function renderArticleCard(hit) {
    const imageUrl = hit.image || hit.featured_image || '';
    const title = hit.title || '';
    const blogHandle = getBlogHandle(hit);
    const url = hit.url || (blogHandle && hit.handle ? `/blogs/${blogHandle}/${hit.handle}` : '#');

    const author = hit.author || 'duPont REGISTRY';
    const authorName = typeof author === 'object' ? (author.name || author.author_name) : author;

    let publishDate = '';
    if (hit.published_at || hit.created_at) {
      const dateObj = new Date(hit.published_at || hit.created_at);
      publishDate = dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    const blogTitle = hit.blog?.title || formatBlogHandle(blogHandle) || '';

    return `
      <article class="card card--article card--style-no_padding card--text-display--title">
        <a class="card__image" href="${url}">
          <figure class="aspect-ratio--landscape aspect-ratio__mobile--widescreen">
            ${imageUrl ? `<img src="${imageUrl}" alt="${escapeHtml(title)}" class="img-absolute" loading="lazy" width="800" height="450">` : ''}
          </figure>
        </a>
        <a href="${url}" class="card__content">
          <div>
            <p class="card__blog-title text-xs">
              ${blogTitle ? `${escapeHtml(blogTitle)}` : ''}
              ${authorName ? ` &ndash; by ${escapeHtml(authorName)}` : ''}
              ${publishDate ? ` &ndash; ${publishDate}` : ''}
            </p>
            <p class="card__title">${escapeHtml(title)}</p>
          </div>
          <div class="card__bottom">
            <p class="button button--primary">${window.searchStrings?.read_more || 'Read More'}</p>
          </div>
        </a>
      </article>
    `;
  }

  function renderPagination() {
    const paginationContainer = document.getElementById('all-news-pagination');
    if (!paginationContainer) return;

    if (totalPages <= 1) {
      paginationContainer.innerHTML = '';
      return;
    }

    let html = '<nav class="pagination" role="navigation" aria-label="Pagination">';
    html += '<ul role="list">';

    // Previous
    if (currentPage > 0) {
      html += `
        <li class="prev">
          <a href="#" data-page="${currentPage - 1}" aria-label="Previous page">
            &larr; <span class="hide-mobile">Previous</span>
          </a>
        </li>
      `;
    }

    // Page numbers (show max 5)
    const maxVisible = 5;
    let startPage = Math.max(0, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages - 1, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(0, endPage - maxVisible + 1);
    }

    if (startPage > 0) {
      html += `<li><a href="#" data-page="0">1</a></li>`;
      if (startPage > 1) html += '<li><span>...</span></li>';
    }

    for (let i = startPage; i <= endPage; i++) {
      if (i === currentPage) {
        html += `<li><span class="pagination__item--current" aria-current="page">${i + 1}</span></li>`;
      } else {
        html += `<li><a href="#" data-page="${i}">${i + 1}</a></li>`;
      }
    }

    if (endPage < totalPages - 1) {
      if (endPage < totalPages - 2) html += '<li><span>...</span></li>';
      html += `<li><a href="#" data-page="${totalPages - 1}">${totalPages}</a></li>`;
    }

    // Next
    if (currentPage < totalPages - 1) {
      html += `
        <li class="next">
          <a href="#" data-page="${currentPage + 1}" aria-label="Next page">
            <span class="hide-mobile">Next</span> &rarr;
          </a>
        </li>
      `;
    }

    html += '</ul></nav>';
    paginationContainer.innerHTML = html;

    // Attach pagination listeners
    paginationContainer.querySelectorAll('a[data-page]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        currentPage = parseInt(link.dataset.page, 10);
        performSearch();
        document.getElementById('all-news-grid')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  // Convert display name to handle format for comparison (e.g., "For Sale" -> "for-sale")
  function displayNameToHandle(name) {
    if (!name) return '';
    return name.toLowerCase().replace(/\s+/g, '-');
  }

  // Check if a blog handle is in the allowed tags list
  function isTagAllowed(blogHandle) {
    // If no allowed tags configured, show all
    if (!allowedTags || allowedTags.length === 0) return true;

    const handleLower = blogHandle.toLowerCase();
    return allowedTags.some(tag => {
      const tagHandle = displayNameToHandle(tag);
      return handleLower === tagHandle || handleLower === tag.toLowerCase();
    });
  }

  function updateBlogFilterOptions(blogFacets) {
    const container = document.getElementById('all-news-filter-options');
    if (!container) return;

    let html = `<button class="all-news-filter__chip ${!currentFilters.blog ? 'active' : ''}" data-blog="">All</button>`;

    // Sort by count (most articles first)
    const sorted = Object.entries(blogFacets).sort((a, b) => b[1] - a[1]);

    // Filter to only allowed tags if configured
    const filtered = sorted.filter(([blogHandle]) => isTagAllowed(blogHandle));

    filtered.forEach(([blogHandle]) => {
      const isActive = currentFilters.blog === blogHandle;
      const displayName = formatBlogHandle(blogHandle);
      html += `
        <button class="all-news-filter__chip ${isActive ? 'active' : ''}" data-blog="${escapeHtml(blogHandle)}">
          ${displayName}
        </button>
      `;
    });

    container.innerHTML = html;
    attachBlogFilterListeners();
  }


  function setupFilterListeners() {
    // Toggle filter panel
    const toggleBtn = document.querySelector('.all-news-filters__toggle');
    const filterPanel = document.querySelector('.all-news-filters__panel');

    if (toggleBtn && filterPanel) {
      toggleBtn.addEventListener('click', () => {
        const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
        toggleBtn.setAttribute('aria-expanded', !isExpanded);
        filterPanel.hidden = isExpanded;
      });
    }

    // Clear filters button
    const clearBtn = document.getElementById('all-news-clear-filters');
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        currentFilters = { blog: '' };
        currentPage = 0;

        // Reset UI
        document.querySelectorAll('.all-news-filter__chip').forEach(chip => {
          chip.classList.toggle('active', chip.dataset.blog === '');
        });

        performSearch();
      });
    }

    attachBlogFilterListeners();
  }

  function attachBlogFilterListeners() {
    document.querySelectorAll('.all-news-filter__chip[data-blog]').forEach(chip => {
      chip.addEventListener('click', () => {
        currentFilters.blog = chip.dataset.blog;
        currentPage = 0;

        // Update active state
        document.querySelectorAll('.all-news-filter__chip[data-blog]').forEach(c => {
          c.classList.toggle('active', c.dataset.blog === currentFilters.blog);
        });

        performSearch();
      });
    });
  }

  function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function formatBlogHandle(handle) {
    if (!handle) return '';
    return handle
      .replace(/-/g, ' ')
      .replace(/\b\w/g, c => c.toUpperCase());
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllNews);
  } else {
    initAllNews();
  }
})();
